import React, { useState, useEffect } from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';
import '../styles/Loading.css';
import './StartupScreen.css';

/**
 * StartupScreen - Displayed while waiting for the backend to be ready
 * Also handles offline state display with auto-retry
 */
function StartupScreen({ message = 'Starting up...', isOffline = false, isServerUnavailable = false, onRetry, autoRetry = true }) {
  const [isChecking, setIsChecking] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetry = async () => {
    if (isChecking) return;
    
    setIsChecking(true);
    if (onRetry) {
      await onRetry();
    }
    setRetryCount(prev => prev + 1);
    // If onRetry doesn't navigate away, stop the checking state after a delay
    setTimeout(() => setIsChecking(false), 2000);
  };

  // Auto-retry with exponential backoff when server is unavailable
  // Starts at 15s, doubles each time up to max 2 minutes, with random jitter
  useEffect(() => {
    if (!autoRetry || isOffline || !isServerUnavailable) return;
    
    // Calculate delay: 15s, 30s, 60s, 120s (max)
    const baseDelay = 15000;
    const maxDelay = 120000;
    const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
    
    // Add random jitter (±20%) to prevent thundering herd
    const jitter = delay * 0.2 * (Math.random() - 0.5);
    const finalDelay = delay + jitter;
    
    const timeout = setTimeout(() => {
      if (!isChecking && onRetry) {
        setIsChecking(true);
        onRetry().finally(() => {
          setTimeout(() => setIsChecking(false), 1000);
        });
      }
    }, finalDelay);

    return () => clearTimeout(timeout);
  }, [autoRetry, isOffline, isServerUnavailable, isChecking, onRetry, retryCount]);

  const showOfflineState = isOffline || isServerUnavailable;
  const offlineTitle = isServerUnavailable ? "Server Unavailable" : "You're Offline";
  const offlineDesc = isServerUnavailable 
    ? "Unable to connect to the Remidy server. Please try again later."
    : "Remidy needs an internet connection to manage your bills and payments.";

  return (
    <div className="startup-screen">
      {/* Animated background blobs - matches login page */}
      <div className="animated-bg">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
        <div className="blob blob-4"></div>
      </div>
      
      <div className="startup-content">
        <div className="startup-logo">
          <h1>Remidy</h1>
          <p className="startup-tagline">Bill Tracking Made Simple</p>
        </div>
        
        {showOfflineState ? (
          <>
            <div className={`startup-offline-icon ${isChecking ? 'checking' : ''}`}>
              {isChecking ? (
                <span className="css-spinner spinner-xl"></span>
              ) : (
                <WifiOff size={48} />
              )}
            </div>
            <p className="startup-message startup-offline-title">{offlineTitle}</p>
            <p className="startup-offline-desc">
              {isChecking ? 'Checking connection...' : offlineDesc}
            </p>
            <button 
              className={`startup-retry-btn ${isChecking ? 'checking' : ''}`} 
              onClick={handleRetry}
              disabled={isChecking}
            >
              {isChecking ? (
                <>
                  <span className="css-spinner spinner-sm"></span>
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw size={18} />
                  Try Again
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="startup-spinner">
              <span className="css-spinner spinner-xl"></span>
            </div>
            <p className="startup-message">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default StartupScreen;
